<script lang="ts" setup>
defineOptions({ name: 'CareerDetailPage' })

import { reactive, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Icon } from '@/components/Icon'
import { jobs } from '../data/portalData'
import { ElMessage } from 'element-plus'
import { getSubmitErrorMessage, submitPortalForm } from '../usePortalSubmission'

const route = useRoute()
const router = useRouter()

const jobId = computed(() => route.params.id as string)
const job = computed(() => jobs.find((j) => j.id === jobId.value))

const form = reactive({
  name: '',
  phone: '',
  email: '',
  intro: ''
})

function goBack() {
  router.push('/portal/careers')
}

async function handleSubmit() {
  if (!form.name || !form.phone || !form.email) {
    ElMessage.warning('请填写姓名、联系电话和邮箱')
    return
  }
  try {
    await submitPortalForm({
      username: form.name,
      tel: form.phone,
      email: form.email,
      content: form.intro,
      productName: job.value?.title,
      type: 'JOB_APPLICATION'
    })
    ElMessage.success('简历投递成功！我们会尽快联系您')
    Object.assign(form, { name: '', phone: '', email: '', intro: '' })
  } catch (error) {
    ElMessage.error(getSubmitErrorMessage(error))
  }
}
</script>

<template>
  <div class="career-detail-page">
    <template v-if="job">
      <div class="content-wrap">
        <button class="back-btn" @click="goBack">&#8592; 返回职位列表</button>

        <!-- Job Info Card -->
        <div class="job-info-card">
          <h1 class="job-title">{{ job.title }}</h1>
          <div class="job-tags">
            <span
              class="job-tag job-tag-dept"
              :style="{ background: job.deptColor + '1A', color: job.deptColor }"
            >
              {{ job.department }}
            </span>
            <span class="job-tag job-tag-default">{{ job.location }}</span>
            <span class="job-tag job-tag-default">{{ job.experience }}</span>
            <span class="job-tag job-tag-default">{{ job.education }}</span>
            <span class="job-tag job-tag-salary">{{ job.salary }}</span>
          </div>
          <p class="job-desc">{{ job.desc }}</p>
          <h3 class="job-requirements-title">任职要求</h3>
          <ul class="job-requirements-list">
            <li v-for="req in job.requirements" :key="req">{{ req }}</li>
          </ul>
        </div>

        <!-- Application Form -->
        <div class="form-card">
          <h3 class="form-title">投递简历</h3>
          <div class="form-grid-2">
            <div class="form-group">
              <label class="form-label">姓名 *</label>
              <input v-model="form.name" class="form-input" placeholder="请输入姓名" />
            </div>
            <div class="form-group">
              <label class="form-label">联系电话 *</label>
              <input
                v-model="form.phone"
                class="form-input"
                type="tel"
                placeholder="请输入手机号"
              />
            </div>
            <div class="form-group">
              <label class="form-label">邮箱地址 *</label>
              <input
                v-model="form.email"
                class="form-input"
                type="email"
                placeholder="请输入邮箱"
              />
            </div>
            <div class="form-group">
              <label class="form-label">简历附件</label>
              <input type="file" accept=".pdf,.doc,.docx" class="form-file-input" />
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">自我介绍</label>
            <textarea
              v-model="form.intro"
              class="form-input form-textarea"
              placeholder="简单介绍你的工作经历和优势"
            />
          </div>
          <button class="form-submit-btn" @click="handleSubmit">投递简历</button>
        </div>
      </div>
    </template>

    <!-- Fallback: job not found -->
    <template v-else>
      <div class="fallback-wrap">
        <h2 class="fallback-title">职位未找到</h2>
        <p class="fallback-desc">该职位不存在或已被下线</p>
        <button class="fallback-btn" @click="goBack">&#8592; 返回职位列表</button>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.content-wrap {
  padding: 64px 24px;
  max-width: 800px;
  margin: 0 auto;
}

.back-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #0066ff;
  font-size: 14px;
  margin-bottom: 24px;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.7;
  }
}

.job-info-card {
  background: #f9fafb;
  border-radius: 16px;
  padding: 32px;
  border: 1px solid #e5e7eb;
  margin-bottom: 24px;
}

.job-title {
  font-size: 28px;
  font-weight: 800;
  margin-bottom: 12px;
}

.job-tags {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.job-tag {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.job-tag-dept {
  // dynamic color from inline style
}

.job-tag-default {
  background: #f3f4f6;
  color: #4b5563;
}

.job-tag-salary {
  background: #ecfdf5;
  color: #00b96b;
}

.job-desc {
  font-size: 15px;
  color: #4b5563;
  line-height: 1.8;
  margin-bottom: 20px;
}

.job-requirements-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 12px;
}

.job-requirements-list {
  list-style: disc;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  li {
    font-size: 14px;
    color: #4b5563;
  }
}

.form-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 32px;
}

.form-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 20px;
}

.form-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.3s ease;

  &:focus {
    border-color: #0066ff;
  }

  &::placeholder {
    color: #9ca3af;
  }
}

.form-textarea {
  min-height: 100px;
  resize: vertical;
}

.form-file-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
}

.form-submit-btn {
  height: 48px;
  padding: 0 40px;
  background: #0066ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #0044cc;
  }
}

.fallback-wrap {
  text-align: center;
  min-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.fallback-title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 12px;
}

.fallback-desc {
  color: #6b7280;
  margin-bottom: 24px;
}

.fallback-btn {
  background: none;
  border: 1px solid #0066ff;
  color: #0066ff;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;

  &:hover {
    background: #0066ff;
    color: #fff;
  }
}

@media (max-width: 768px) {
  .form-grid-2 {
    grid-template-columns: 1fr;
  }

  .job-title {
    font-size: 22px;
  }
}
</style>
